import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CsvFile } from '@/types/csvFiles';
import { Alert, Button, EmptyContent, Flex } from '@/components/ui';
import { usePermissionAccess } from '@/hooks/usePermissionAccess';
import { PERMISSIONS } from '@/types/permissions';
import { useCsvFiles, useDeleteCsvFile } from '@/services/csvFilesHooks';
import { uploadCsvFile, validateCsvFile } from '@/services/csvFilesApi';
import { toast } from '@/utils/toast';
import { runPoolSettled } from '@/utils/concurrencyPool';
import { getUploadMaxConcurrent } from '@/utils/uploadPreferences';

import {
  IconAlertTriangleFilled,
  IconCloudUpload,
  IconUpload,
} from '@tabler/icons-react';
import { useDropzone } from 'react-dropzone';

import CsvFilesTable from './CsvFilesTable';
import { CsvUploadConfirmationModal } from './CsvUploadConfirmationModal';
import { BulkDeleteConfirmationModal } from './BulkDeleteConfirmationModal';
import {
  CsvFilesManagerProps,
  FileUploadProgress,
  FileUploadStatus,
} from './types';

const CsvFilesManager: React.FC<CsvFilesManagerProps> = ({ className }) => {
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileProgress, setFileProgress] = useState<FileUploadProgress[]>([]);
  const [currentUploadingFileIndex, setCurrentUploadingFileIndex] = useState<
    number | undefined
  >(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadAbortControllersRef = useRef<Map<number, AbortController>>(new Map());
  const batchAbortControllerRef = useRef<AbortController | null>(null);
  const cancelUploadRequestedRef = useRef(false);
  const pendingStatusRef = useRef<
    Map<number, { status: FileUploadStatus; progress?: number; error?: string }>
  >(new Map());
  const statusFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Throttled status batching to avoid O(n) setState on every completion
  const throttleStatusMs = 200;
  const scheduleStatusFlush = useCallback(() => {
    if (statusFlushTimerRef.current) return;
    statusFlushTimerRef.current = setTimeout(() => {
      const pending = new Map(pendingStatusRef.current);
      pendingStatusRef.current.clear();
      statusFlushTimerRef.current = null;
      if (pending.size === 0) return;
      setFileProgress(prev =>
        prev.map((fp, idx) => {
          const update = pending.get(idx);
          if (!update) return fp;
          return {
            ...fp,
            status: update.status,
            progress: update.progress ?? fp.progress,
            error: update.error,
          };
        })
      );
    }, throttleStatusMs);
  }, []);

  const throttledStatusUpdate = useCallback(
    (
      index: number,
      status: FileUploadStatus,
      progress?: number,
      error?: string
    ) => {
      pendingStatusRef.current.set(index, {
        status,
        progress,
        error,
      });
      scheduleStatusFlush();
    },
    [scheduleStatusFlush]
  );

  useEffect(
    () => () => {
      if (statusFlushTimerRef.current) {
        clearTimeout(statusFlushTimerRef.current);
        statusFlushTimerRef.current = null;
      }
    },
    []
  );

  // Bulk delete state
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [filesToDelete, setFilesToDelete] = useState<CsvFile[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentDeletingFile, setCurrentDeletingFile] =
    useState<CsvFile | null>(null);
  const [deleteProgress, setDeleteProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);

  // API hooks - memoize filters to prevent unnecessary requests
  const csvFilesFilters = useMemo(
    () => ({
      page: pagination.pageIndex + 1, // API uses 1-based pagination
      per_page: pagination.pageSize,
    }),
    [pagination.pageIndex, pagination.pageSize]
  );

  const { data, isLoading, error, refetch } = useCsvFiles(csvFilesFilters);
  const files = data?.data || [];

  const paginationInfo = data?.pagination;
  const deleteMutation = useDeleteCsvFile();

  // Permission checks
  // TODO: Migrate to new permissions - canImportCSV is deprecated
  const _canImportCSV = usePermissionAccess([
    PERMISSIONS.CSV_ACCESS,
    PERMISSIONS.CSV_READ_ALL,
  ]);
  // New permission for upload access
  const canUploadCSV = usePermissionAccess([PERMISSIONS.CSV_UPLOAD]);
  const _canManageStudyFiles = usePermissionAccess([
    PERMISSIONS.STUDY_FILES_ACCESS,
  ]);
  // Permission for CSV delete access
  const canDeleteCSV = usePermissionAccess([PERMISSIONS.CSV_DELETE]);

  const handleFilesSelected = useCallback((selectedFiles: File[]) => {
    if (selectedFiles.length === 0) return;

    // Validate CSV files
    const validFiles: File[] = [];
    const invalidFiles: File[] = [];

    selectedFiles.forEach(file => {
      const validation = validateCsvFile(file);
      if (validation.isValid) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file);
      }
    });

    if (invalidFiles.length > 0) {
      const fileNames = invalidFiles.map(f => f.name).join(', ');
      setDeleteError(
        `Invalid files rejected: ${fileNames}. Only CSV files are allowed.`
      );
    }

    if (validFiles.length > 0) {
      setSelectedFiles(validFiles);
      setShowUploadModal(true);
    }
  }, []);

  const handleDeleteFile = useCallback(
    async (file: CsvFile) => {
      setDeleteError(null);

      try {
        await deleteMutation.mutateAsync(file.id);
      } catch (error) {
        setDeleteError(
          error instanceof Error ? error.message : 'Delete failed'
        );
      }
    },
    [deleteMutation]
  );

  // Bulk delete handler with queue
  const handleBulkDelete = useCallback(async (files: CsvFile[]) => {
    if (files.length === 0) return;

    setFilesToDelete(files);
    setShowBulkDeleteModal(true);
  }, []);

  const handleBulkDeleteConfirm = useCallback(async () => {
    if (filesToDelete.length === 0) return;

    setIsDeleting(true);
    setDeleteError(null);
    setDeleteProgress({ current: 0, total: filesToDelete.length });
    setCurrentDeletingFile(null);

    const errors: Array<{ file: CsvFile; error: Error }> = [];

    // Process files sequentially
    for (let i = 0; i < filesToDelete.length; i++) {
      const file = filesToDelete[i];
      setCurrentDeletingFile(file);
      setDeleteProgress({ current: i + 1, total: filesToDelete.length });

      try {
        await deleteMutation.mutateAsync(file.id);
        // Small delay to prevent overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        errors.push({
          file,
          error: error instanceof Error ? error : new Error('Delete failed'),
        });
      }
    }

    setIsDeleting(false);
    setCurrentDeletingFile(null);
    setDeleteProgress(null);

    // Show errors if any
    if (errors.length > 0) {
      const errorMessages = errors
        .map(e => `${e.file.filename}: ${e.error.message}`)
        .join('\n');
      setDeleteError(
        `Failed to delete ${errors.length} file(s):\n${errorMessages}`
      );
    }

    // Close modal and reset state after a short delay to allow UI to update
    setTimeout(() => {
      setShowBulkDeleteModal(false);
      setFilesToDelete([]);
      // Selection will be cleared automatically when files list updates
    }, 500);
  }, [filesToDelete, deleteMutation]);

  const handleBulkDeleteModalClose = useCallback(() => {
    if (!isDeleting) {
      setShowBulkDeleteModal(false);
      setFilesToDelete([]);
      setDeleteProgress(null);
      setCurrentDeletingFile(null);
    }
  }, [isDeleting]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handlePaginationChange = useCallback(
    (newPagination: { pageIndex: number; pageSize: number }) => {
      setPagination(newPagination);
    },
    []
  );

  // Upload handlers
  const handleConfirmUpload = useCallback(async () => {
    if (selectedFiles.length === 0) return;

    cancelUploadRequestedRef.current = false;

    // Initialize file progress
    const initialProgress: FileUploadProgress[] = selectedFiles.map(file => ({
      file,
      status: 'pending' as FileUploadStatus,
      progress: 0,
    }));
    setFileProgress(initialProgress);
    setCurrentUploadingFileIndex(0);

    try {
      const maxConcurrent = getUploadMaxConcurrent();
      const activeIndices = new Set<number>();

      const updateCurrentUploadingIndex = () => {
        if (activeIndices.size === 0) {
          setCurrentUploadingFileIndex(undefined);
          return;
        }
        setCurrentUploadingFileIndex(Math.min(...Array.from(activeIndices)));
      };

      uploadAbortControllersRef.current.clear();
      batchAbortControllerRef.current = new AbortController();

      let successCount = 0;
      let errorCount = 0;

      const indices = selectedFiles.map((_, idx) => idx);

      await runPoolSettled(
        indices,
        async (i: number) => {
          if (cancelUploadRequestedRef.current) {
            throw new DOMException('Aborted', 'AbortError');
          }

          const file = selectedFiles[i];
          if (!file) return;

          activeIndices.add(i);
          updateCurrentUploadingIndex();

          throttledStatusUpdate(i, 'uploading' as FileUploadStatus, 0);

          const controller = new AbortController();
          uploadAbortControllersRef.current.set(i, controller);

          try {
            await uploadCsvFile(file, { signal: controller.signal });

            successCount += 1;
            throttledStatusUpdate(i, 'completed' as FileUploadStatus, 100);
          } catch (error) {
            const isCancelled =
              (error instanceof Error &&
                (error.name === 'CanceledError' ||
                  error.name === 'AbortError')) ||
              (error &&
                typeof error === 'object' &&
                'code' in error &&
                (error as { code?: unknown }).code === 'ERR_CANCELED');

            if (isCancelled || cancelUploadRequestedRef.current) {
              throttledStatusUpdate(i, 'cancelled' as FileUploadStatus);
              cancelUploadRequestedRef.current = true;
              batchAbortControllerRef.current?.abort();
              throw error;
            }

            errorCount += 1;
            const errorMessage =
              error instanceof Error ? error.message : 'Upload failed';

            throttledStatusUpdate(
              i,
              'error' as FileUploadStatus,
              undefined,
              errorMessage
            );
          } finally {
            uploadAbortControllersRef.current.delete(i);
            activeIndices.delete(i);
            updateCurrentUploadingIndex();
          }
        },
        { concurrency: maxConcurrent, signal: batchAbortControllerRef.current.signal }
      );

      batchAbortControllerRef.current = null;

      if (cancelUploadRequestedRef.current) {
        setFileProgress(prev =>
          prev.map(fp =>
            fp.status === 'pending' || fp.status === 'uploading'
              ? { ...fp, status: 'cancelled' as FileUploadStatus }
              : fp
          )
        );
        setCurrentUploadingFileIndex(undefined);
        return;
      }

      // Invalidate CSV files list
      refetch();

      // Show appropriate toast messages
      if (errorCount === 0) {
        toast.success(
          `${selectedFiles.length} CSV file(s) uploaded successfully`,
          {
            description: 'File(s) are ready for use',
          }
        );
      } else if (successCount === 0) {
        toast.error(`Failed to upload all ${selectedFiles.length} CSV files`, {
          description: 'Please check file formats and try again',
        });
      } else {
        toast.warning(
          `${successCount} of ${selectedFiles.length} files uploaded`,
          {
            description: `${errorCount} files failed to upload`,
          }
        );
      }

      // Close modal after a short delay to show final status
      setTimeout(() => {
        setShowUploadModal(false);
        setSelectedFiles([]);
        setFileProgress([]);
        setCurrentUploadingFileIndex(undefined);
      }, 1000);
    } catch (error) {
      toast.error('Upload failed', {
        description:
          error instanceof Error ? error.message : 'Please try again',
      });
      setFileProgress([]);
      setCurrentUploadingFileIndex(undefined);
    }
  }, [selectedFiles, refetch, throttledStatusUpdate]);

  const handleCancelUpload = useCallback(() => {
    cancelUploadRequestedRef.current = true;
    batchAbortControllerRef.current?.abort();
    uploadAbortControllersRef.current.forEach(controller => controller.abort());
    uploadAbortControllersRef.current.clear();

    setFileProgress(prev =>
      prev.map(fp =>
        fp.status === 'pending' || fp.status === 'uploading'
          ? { ...fp, status: 'cancelled' as FileUploadStatus }
          : fp
      )
    );
    setCurrentUploadingFileIndex(undefined);
    toast.info('Upload cancelled');
  }, []);

  const handleCloseUploadModal = useCallback(() => {
    const isUploading = fileProgress.some(
      fp => fp.status === 'uploading' || fp.status === 'pending'
    );
    if (!isUploading) {
      setShowUploadModal(false);
      setSelectedFiles([]);
      setFileProgress([]);
      setCurrentUploadingFileIndex(undefined);
    }
  }, [fileProgress]);

  const handleRemoveFileFromSelection = useCallback((index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setFileProgress(prev => prev.filter((_, i) => i !== index));
  }, []);

  useEffect(() => {
    if (showUploadModal && selectedFiles.length === 0) {
      setShowUploadModal(false);
    }
  }, [showUploadModal, selectedFiles.length]);

  // Drag and drop handlers
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      handleFilesSelected(acceptedFiles);
    },
    [handleFilesSelected]
  );

  const onDragEnter = useCallback(() => setDragActive(true), []);
  const onDragLeave = useCallback(() => setDragActive(false), []);
  const onDropAccepted = useCallback(() => setDragActive(false), []);
  const onDropRejected = useCallback(() => setDragActive(false), []);

  const dropzoneConfig = useMemo(
    () => ({
      onDrop,
      noClick: true,
      noKeyboard: true,
      multiple: true,
      accept: {
        'text/csv': ['.csv'],
      },
      onDragEnter,
      onDragLeave,
      onDropAccepted,
      onDropRejected,
    }),
    [onDrop, onDragEnter, onDragLeave, onDropAccepted, onDropRejected]
  );

  const { getRootProps, getInputProps, isDragActive } =
    useDropzone(dropzoneConfig);

  // Upload button handler
  const handleUploadButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFilesSelected(Array.from(e.target.files));
        e.target.value = '';
      }
    },
    [handleFilesSelected]
  );

  // Memoize actions to prevent unnecessary re-renders
  const tableActions = useMemo(
    () => (
      <Flex gap="2" align="center">
        {canUploadCSV && (
          <Button
            startIcon={<IconUpload />}
            variant="classic"
            size="small"
            color="secondary"
            onClick={handleUploadButtonClick}
          >
            Upload CSV Files
          </Button>
        )}
      </Flex>
    ),
    [canUploadCSV, handleUploadButtonClick]
  );

  return (
    <div
      {...getRootProps()}
      className={className}
      style={{
        position: 'relative',
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'stretch',
      }}
    >
      <input {...getInputProps()} />

      {/* Drag and drop overlay */}
      {(dragActive || isDragActive) && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundImage:
              'linear-gradient(to bottom, var(--accent-2), var(--accent-3))',
            backdropFilter: 'blur(4px)',
          }}
        >
          <EmptyContent
            withIconBox
            icon={<IconCloudUpload style={{ color: 'var(--accent-9)' }} />}
            title="Drop CSV files here to upload"
            description="Release to select your CSV files for upload"
            size="large"
            disableAnimation
          />
        </div>
      )}

      {/* Hidden file input for CSV files only */}
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        multiple
        accept=".csv,text/csv"
        onChange={handleFileInputChange}
        className="file-upload-input"
      />

      {/* Delete errors */}
      {deleteError && (
        <Alert color="error" title="Error">
          {deleteError}
        </Alert>
      )}

      {/* General API errors */}
      {error && (
        <Alert
          color="error"
          title="Failed to load CSV files. Please try again."
          variant="soft"
          icon={<IconAlertTriangleFilled />}
        />
      )}

      {/* CSV Files table */}

      <CsvFilesTable
        files={files}
        loading={isLoading}
        onDeleteFile={canDeleteCSV ? handleDeleteFile : undefined}
        onBulkDelete={canDeleteCSV ? handleBulkDelete : undefined}
        onRefresh={handleRefresh}
        pagination={paginationInfo}
        paginationState={pagination}
        onPaginationChange={handlePaginationChange}
        actions={tableActions}
        onUploadClick={canUploadCSV ? handleUploadButtonClick : undefined}
        isDeleting={isDeleting}
      />

      {/* Upload Confirmation Modal */}
      <CsvUploadConfirmationModal
        isOpen={showUploadModal}
        onClose={handleCloseUploadModal}
        onConfirm={handleConfirmUpload}
        onCancelUpload={handleCancelUpload}
        onRemoveFile={handleRemoveFileFromSelection}
        files={selectedFiles}
        isUploading={fileProgress.some(
          fp => fp.status === 'uploading' || fp.status === 'pending'
        )}
        fileProgress={fileProgress}
        currentUploadingFileIndex={currentUploadingFileIndex}
      />

      {/* Bulk Delete Confirmation Modal */}
      <BulkDeleteConfirmationModal
        isOpen={showBulkDeleteModal}
        onClose={handleBulkDeleteModalClose}
        onConfirm={handleBulkDeleteConfirm}
        files={filesToDelete}
        isDeleting={isDeleting}
        currentDeletingFile={currentDeletingFile}
        progress={deleteProgress || undefined}
      />
    </div>
  );
};

export default CsvFilesManager;
